/* Serial Seal — designer artwork registry.
 *
 * Two sources, one lookup:
 *   SS_ART.baked  — finished illustrated fragments, authored art keyed by design.
 *                   These are plain SVG strings in the 100×117 art-window box,
 *                   using {{color_hex}} for the customer's accent.
 *   SS_ART.motif  — procedural fallbacks so a design is never a blank rectangle
 *                   while its illustration is still being drawn.
 *
 * THE MATERIAL RULE applies to everything here: the window starts as bare
 * holographic foil, ink is opaque near-black, and unpainted area IS the shine.
 * You paint darkness around light, never light onto darkness.
 */
window.SS_ART = (function () {
  'use strict';

  var INK = '#0c0d13', INK2 = '#14161f', INK3 = '#1c1f2b';
  var L1 = '#e9eaf2', L2 = '#a9b0c6';
  var W = 100, H = 117;

  function r2(n) { return Math.round(n * 100) / 100; }
  function rng(seed) {
    var a = 0x811c9dc5; seed = String(seed);
    for (var i = 0; i < seed.length; i++) { a ^= seed.charCodeAt(i); a = Math.imul(a, 0x01000193); }
    return function () {
      a |= 0; a = (a + 0x6D2B79F5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  /* an inked field with holes punched through it — the holes are live foil.
     op < 1 smokes the field so foil glows through the ink itself (holo-heavy:
     55% coverage prints as tinted shimmer instead of a solid black flood). */
  function cut(holes, fill, op) {
    return '<path fill-rule="evenodd" fill="' + (fill || INK) + '"' +
      (op ? ' opacity="' + op + '"' : '') + ' d="M0 0H' + W +
      'V' + H + 'H0Z ' + holes.join(' ') + '"/>';
  }
  function circPath(cx, cy, r) {
    return 'M' + r2(cx - r) + ' ' + r2(cy) + 'A' + r + ' ' + r + ' 0 1 0 ' + r2(cx + r) + ' ' + r2(cy) +
      'A' + r + ' ' + r + ' 0 1 0 ' + r2(cx - r) + ' ' + r2(cy) + 'Z';
  }

  /* ——————————— procedural motifs ———————————
     Each returns an SVG fragment for the 100×117 window. They are deliberately
     restrained: bare foil carries the design, ink gives it structure. */
  var motif = {
    /* ——————— CARD-FOCUSED MOTIFS ———————
       The hobby's own objects: slabs, packs, card backs, stacks, the grader's
       reticle. Same material rule as everything above — ink is painted around
       the shine, never onto it. Added 2026-07-29. */
/* graded card slab — rigid portrait holder, ruled label bar over a live-foil
       card window. The shell is a foil silhouette on a smoked field; the card
       and the label are punched clean through the frame so both flash. */
    slab: function (seed) {
      var rnd = rng(seed || 'slab'), o = '';
      function rr(x, y, w, h, r) {                       // rounded rect as path 'd'
        return 'M' + r2(x + r) + ' ' + r2(y) + 'h' + r2(w - r * 2) +
          'a' + r + ' ' + r + ' 0 0 1 ' + r + ' ' + r + 'v' + r2(h - r * 2) +
          'a' + r + ' ' + r + ' 0 0 1 ' + (-r) + ' ' + r + 'h' + r2(-(w - r * 2)) +
          'a' + r + ' ' + r + ' 0 0 1 ' + (-r) + ' ' + (-r) + 'v' + r2(-(h - r * 2)) +
          'a' + r + ' ' + r + ' 0 0 1 ' + r + ' ' + (-r) + 'Z';
      }
      var shell = rr(14, 8, 72, 101, 6.5);               // outer acrylic silhouette
      var body  = rr(17.5, 11.5, 65, 94, 4.8);           // inked frame
      var hairO = rr(20, 14, 60, 89, 3.2);               // 1.2mm foil hairline ring
      var hairI = rr(21.2, 15.2, 57.6, 86.6, 2.5);
      var label = rr(23, 17, 54, 22, 1.6);
      var card  = rr(23, 44, 54, 50, 1.3);
      var cert  = ['M27 96h20v2.4h-20Z', 'M51 96h11v2.4h-11Z', circPath(71, 97.2, 1.5)];

      o += cut([shell], INK, 0.45);                      // field smoked, slab = foil
      /* one evenodd stack: frame inked, hairline + windows + cert marks cut to foil */
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="' + body + ' ' + hairO + ' ' +
        hairI + ' ' + label + ' ' + card + ' ' + cert.join(' ') + '"/>';
      o += '<path d="' + shell + '" fill="none" stroke="{{color_hex}}" stroke-width="1.5"/>';

      /* label bar: ruled ink lines standing in for text, never real glyphs */
      o += '<path fill="' + INK + '" d="M27 21.5h30v2.3h-30Z M27 27h25v1.6h-25Z M27 31.7h19v1.6h-19Z"/>';
      var block = rr(60.5, 19, 14.5, 18, 1.5);
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="' + block +
        ' M63 22.5h9.5v2.2h-9.5Z M63 27.5h9.5v6h-9.5Z"/>';   // grade stamp, foil notches
      o += '<path d="' + block + '" fill="none" stroke="{{color_hex}}" stroke-width="0.9"/>';
      o += '<path d="M23 41.5h54" stroke="{{color_hex}}" stroke-width="1.2" opacity="0.9"/>';

      /* card window stays bare foil — only fine ink linework lands on it */
      o += '<rect x="26" y="47" width="48" height="44" fill="none" stroke="' + INK + '" stroke-width="1.1"/>';
      o += '<rect x="30" y="51" width="40" height="25" fill="none" stroke="' + INK + '" stroke-width="0.8" opacity="0.85"/>';
      o += '<path fill="' + INK + '" d="M33 81h34v1.9h-34Z M38 85.4h24v1.4h-24Z"/>';
      [[25, 46, 1, 1], [75, 46, -1, 1], [25, 92, 1, -1], [75, 92, -1, -1]].forEach(function (c) {
        o += '<path d="M' + c[0] + ' ' + r2(c[1] + c[3] * 4.5) + 'V' + c[1] + 'H' +
          r2(c[0] + c[2] * 4.5) + '" fill="none" stroke="' + INK + '" stroke-width="1.4"/>';
      });
      for (var g = 0; g < 3; g++) {                      // case glare ruling, top-right
        var gx = 62 + g * 4.4 + rnd() * 1.6;
        o += '<path d="M' + r2(gx) + ' 48L' + r2(gx - 13) + ' 61" stroke="' + L2 +
          '" stroke-width="0.7" opacity="0.32"/>';
      }
      return o;
    },

    /* grade badge — struck medallion: beaded outer ring, sunburst crown, laurel
       skirt, and an abstract bar-and-ring mark cut out of the inked core. */
    gemmint: function (seed) {
      var rnd = rng(seed || 'gemmint'), cx = 50, cy = 52, o = '', i, a;

      o += cut([circPath(cx, cy, 40)], INK, 0.42);       // disc reads as raw foil
      o += '<circle cx="' + cx + '" cy="' + cy + '" r="40" fill="none" stroke="{{color_hex}}" stroke-width="1.6"/>';

      /* outer band inked, with beads punched back out to foil */
      var band = [circPath(cx, cy, 38), circPath(cx, cy, 33)];
      for (i = 0; i < 24; i++) {
        a = (i / 24) * Math.PI * 2;
        band.push(circPath(r2(cx + Math.cos(a) * 35.5), r2(cy + Math.sin(a) * 35.5), i % 6 === 0 ? 2.5 : 1.25));
      }
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="' + band.join(' ') + '"/>';
      o += '<circle cx="' + cx + '" cy="' + cy + '" r="31.4" fill="none" stroke="' + L2 +
        '" stroke-width="0.55" opacity="0.65"/>';

      /* sunburst over the top half only — laurel owns the bottom */
      for (i = 0; i < 17; i++) {
        a = Math.PI + 0.32 + i * ((Math.PI - 0.64) / 16);
        o += '<path d="M' + r2(cx + Math.cos(a) * 23.6) + ' ' + r2(cy + Math.sin(a) * 23.6) +
          'L' + r2(cx + Math.cos(a) * 32) + ' ' + r2(cy + Math.sin(a) * 32) +
          '" stroke="' + INK + '" stroke-width="' + (i % 2 ? 0.7 : 1.7) + '" opacity="0.95"/>';
      }
      /* laurel skirt, mirrored, opening at bottom centre */
      for (var s = -1; s <= 1; s += 2) {
        for (i = 0; i < 7; i++) {
          var t = 0.30 + i * 0.155;
          var lx = cx + s * Math.cos(t) * 27.4, ly = cy + Math.sin(t) * 27.4;
          var deg = s > 0 ? t * 180 / Math.PI : 180 - t * 180 / Math.PI;
          o += '<ellipse cx="' + r2(lx) + '" cy="' + r2(ly) + '" rx="4.7" ry="2.1" fill="' + INK +
            '" transform="rotate(' + r2(deg - s * 20) + ' ' + r2(lx) + ' ' + r2(ly) + ')"/>';
        }
      }

      /* inked core with the grade mark cut out — a chunky bar and a heavy ring,
         struck shapes rather than a legible numeral */
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="' + circPath(cx, cy, 21.5) +
        ' M35 41h5.8v22h-5.8Z ' + circPath(53.6, 52, 10.6) + ' ' + circPath(53.6, 52, 5.4) + '"/>';
      o += '<circle cx="' + cx + '" cy="' + cy + '" r="23.2" fill="none" stroke="{{color_hex}}" stroke-width="1.3"/>';

      /* exergue banner, its plate slots cut to foil */
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="M14 94h72l-7 7 7 7H14l7-7Z ' +
        'M25 99.2h23v3h-23Z M52 99.2h15v3h-15Z"/>';
      o += '<path d="M19 96.2h62" stroke="' + L2 + '" stroke-width="0.7" opacity="0.7"/>';

      for (i = 0; i < 12; i++) {                         // sparse strike sparkle in the corners
        a = rnd() * Math.PI * 2;
        var rr2 = 42 + rnd() * 16;
        o += '<circle cx="' + r2(cx + Math.cos(a) * rr2) + '" cy="' + r2(cy + Math.sin(a) * rr2 * 0.85) +
          '" r="' + r2(0.6 + rnd() * 0.9) + '" fill="' + L1 + '" opacity="' + r2(0.35 + rnd() * 0.4) + '"/>';
      }
      return o;
    },
/* foil booster pack — serrated crimp top and bottom, hang slot, and
       diagonal sheen bands cut clean through the wrapper to bare foil */
    boosterpack: function (seed) {
      var rnd = rng(seed || 'boosterpack'), o = '', holes = [], i;
      var L = 19, R = 81, T = 9, B = 108;
      var body = 'M21 ' + T + 'H79L' + R + ' 11V106L79 ' + B + 'H21L' + L + ' 106V11Z';

      function bar(x, y, w, h) {
        return 'M' + r2(x) + ' ' + r2(y) + 'h' + r2(w) + 'v' + r2(h) + 'h' + r2(-w) + 'Z';
      }
      function band(xb, xt, w) {                                  // leaning sheen, y 98 → 30
        return 'M' + r2(xb) + ' 98L' + r2(xt) + ' 30h' + r2(w) + 'L' + r2(xb + w) + ' 98Z';
      }

      /* every bright part of the pack is a HOLE in the wrapper ink */
      holes.push(bar(23, 11.6, 54, 2.2));                         // header strip
      holes.push(circPath(50, 16.8, 2.8));                        // euro hang slot
      holes.push(bar(20.5, 19.8, 60, 1.9));                       // crimp rail
      for (i = 0; i < 10; i++) {                                  // serrated crimp teeth
        var x0 = 20.5 + i * 6;
        holes.push('M' + r2(x0) + ' 21.7L' + r2(x0 + 3) + ' 28.4L' + r2(x0 + 6) + ' 21.7Z');
        holes.push('M' + r2(x0) + ' 106.4L' + r2(x0 + 3) + ' 100.4L' + r2(x0 + 6) + ' 106.4Z');
      }
      holes.push(band(27, 51, 12));                               // broad shine
      holes.push(band(45, 69, 5));
      holes.push(band(21.5, 45.5, 2.4));
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="' + body + ' ' + holes.join(' ') + '"/>';

      /* cylindrical sheen: ink hairlines crowd the edges, centre stays open */
      for (i = 0; i < 25; i++) {
        var sx = 21 + i * 2.42, t = Math.abs(sx - 50) / 29;
        o += '<path d="M' + r2(sx) + ' 29.6V99" stroke="' + INK + '" stroke-width="' +
          (i % 2 ? 0.7 : 1.2) + '" opacity="' + r2(0.05 + Math.pow(t, 1.8) * 0.5 + rnd() * 0.06) + '"/>';
      }

      /* emblem — inked disc with a foil diamond punched out of it */
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="' + circPath(50, 57, 15) +
        ' M50 45.5L58.5 57L50 68.5L41.5 57Z"/>';
      o += '<circle cx="50" cy="57" r="15" fill="none" stroke="{{color_hex}}" stroke-width="1.5"/>';
      o += '<circle cx="50" cy="57" r="19" fill="none" stroke="' + L2 + '" stroke-width="0.6" opacity="0.6"/>';

      /* wordmark blocks and the accent rule under them */
      o += '<path d="M31 82h38v3.4h-38Z M37 89.5h26v2.4h-26Z" fill="' + INK + '"/>';
      o += '<path d="M33 95.6h34" stroke="{{color_hex}}" stroke-width="1.4"/>';
      o += '<path d="M23 31V101M77 31V101" stroke="' + L2 + '" stroke-width="0.6" opacity="0.45"/>';

      /* side gutters — vertical ink ribs so the pack reads as sitting on foil */
      for (i = 0; i < 7; i++) {
        var gx = 2.2 + i * 2.5;
        o += '<path d="M' + r2(gx) + ' 4V113M' + r2(100 - gx) + ' 4V113" stroke="' + INK +
          '" stroke-width="1.1" opacity="' + r2(0.5 - i * 0.06) + '"/>';
      }
      return o;
    },

    /* the pull — a jagged tear across the upper third with light bursting out,
       rays fanning down into the field; tear and rays are cut foil */
    /* pack rip — the torn pack mouth across the top, light blasting out of it.
       The pack body is solid ink with a ragged bite out of its lower edge; the
       field below is inked too, with wedge holes fanning from the tear so the
       "light" is literally the foil coming through. */
    packrip: function (seed) {
      var rnd = rng(seed || 'packrip'), o = '', i;

      /* ragged tear line across y~46, walked left to right */
      var tear = 'M0 0H100V38', x = 100, y = 38;
      for (i = 0; i < 13; i++) {
        x = 100 - (i + 1) * (100 / 13);
        y = (i % 2 ? 52 : 40) + rnd() * 4;
        tear += 'L' + r2(x) + ' ' + r2(y);
      }
      tear += 'L0 40Z';
      o += '<path d="' + tear + '" fill="' + INK + '"/>';

      /* crimp teeth along the very top edge of the pack */
      for (i = 0; i < 16; i++) {
        var cx = 3 + i * 6.2;
        o += '<path d="M' + r2(cx) + ' 0l3.1 5.4L' + r2(cx + 6.2) + ' 0Z" fill="' + INK3 + '"/>';
      }
      /* a shine band down the sealed part, cut so it is real foil */
      o += '<path d="M62 0l13 0l-19 34l-13 0Z" fill="' + INK2 + '" opacity="0.85"/>';

      /* the burst: inked lower field with wedge holes fanning from the tear */
      var apex = [50, 47], holes = [], n = 13;
      for (i = 0; i < n; i++) {
        if (i % 2) continue;                              // alternate wedges stay ink
        var a0 = (18 + i * 11.2) * Math.PI / 180;
        var a1 = (18 + (i + 1) * 11.2) * Math.PI / 180;
        var r = 96 + rnd() * 18;
        holes.push('M' + apex[0] + ' ' + apex[1] +
          'L' + r2(apex[0] + Math.cos(a0) * r) + ' ' + r2(apex[1] + Math.sin(a0) * r) +
          'L' + r2(apex[0] + Math.cos(a1) * r) + ' ' + r2(apex[1] + Math.sin(a1) * r) + 'Z');
      }
      o += '<path fill-rule="evenodd" fill="' + INK + '" opacity="0.9" d="M0 44H100V117H0Z ' +
           holes.join(' ') + '"/>';

      /* accent along the tear, and two long spikes of light past the ink */
      o += '<path d="' + tear.replace(/^M0 0H100V38/, 'M100 38').replace(/Z$/, '') +
           '" fill="none" stroke="{{color_hex}}" stroke-width="1.3"/>';
      for (i = 0; i < 2; i++) {
        var aa = (52 + i * 58) * Math.PI / 180;
        o += '<path d="M' + apex[0] + ' ' + apex[1] + 'L' +
             r2(apex[0] + Math.cos(aa) * 96) + ' ' + r2(apex[1] + Math.sin(aa) * 96) +
             '" stroke="{{color_hex}}" stroke-width="0.8" opacity="0.75"/>';
      }
      /* confetti of foil chips thrown out of the tear */
      for (i = 0; i < 9; i++) {
        var px = 12 + rnd() * 76, py = 56 + rnd() * 50, ps = 1.2 + rnd() * 2.2;
        o += '<path d="M' + r2(px) + ' ' + r2(py) + 'l' + r2(ps) + ' ' + r2(ps * 1.6) +
             'l' + r2(-ps) + ' ' + r2(ps * 0.7) + 'l' + r2(-ps) + ' ' + r2(-ps * 1.6) + 'Z" fill="' +
             L2 + '" opacity="0.5"/>';
      }
      return o;
    },
/* classic patterned card back — double-keyline border, a staggered diamond
       lattice woven across the panel, oval rosette medallion at the centre.
       Every keyline, lattice cell and the medallion field are CUT to bare foil;
       the ink is only the ground they sit in. */
    cardback: function () {
      var o = '', H2 = [], cx = 50, cy = 58;

      function rectP(x, y, w, h) {
        return 'M' + r2(x) + ' ' + r2(y) + 'h' + r2(w) + 'v' + r2(h) + 'h' + r2(-w) + 'Z';
      }
      function frame(x, y, w, h, t) {                       // foil keyline ring
        return rectP(x, y, w, h) + ' ' + rectP(x + t, y + t, w - t * 2, h - t * 2);
      }
      function diamond(x, y, rx, ry) {
        return 'M' + r2(x) + ' ' + r2(y - ry) + 'L' + r2(x + rx) + ' ' + r2(y) +
          'L' + r2(x) + ' ' + r2(y + ry) + 'L' + r2(x - rx) + ' ' + r2(y) + 'Z';
      }
      function ell(x, y, rx, ry) {
        return 'M' + r2(x - rx) + ' ' + r2(y) + 'a' + r2(rx) + ' ' + r2(ry) + ' 0 1 0 ' +
          r2(rx * 2) + ' 0a' + r2(rx) + ' ' + r2(ry) + ' 0 1 0 ' + r2(-rx * 2) + ' 0Z';
      }

      H2.push(frame(8, 11, 84, 95, 1.2));                   // outer border rule
      H2.push(frame(12, 15, 76, 87, 1.0));                  // inner panel rule

      /* the weave: brick-staggered diamond outlines, band 0.74 wide. Cells whose
         centre falls in the medallion reserve are dropped so no two foil holes
         ever overlap (an overlap flips evenodd back to solid ink). */
      var rows = [23, 33, 43, 53, 63, 73, 83, 93], r, c, cols, dx, dy;
      for (r = 0; r < rows.length; r++) {
        cols = (r % 2) ? [26, 38, 50, 62, 74] : [20, 32, 44, 56, 68, 80];
        for (c = 0; c < cols.length; c++) {
          dx = (cols[c] - cx) / 24; dy = (rows[r] - cy) / 30.5;
          if (dx * dx + dy * dy < 1) continue;
          H2.push(diamond(cols[c], rows[r], 5.6, 5) + ' ' + diamond(cols[c], rows[r], 4.5, 4));
        }
      }
      H2.push(ell(cx, cy, 18, 25));                         // medallion field = live foil
      o += cut(H2, INK, 0.9);

      o += '<path d="' + ell(cx, cy, 20.6, 28) + '" fill="none" stroke="' + L2 +
        '" stroke-width="0.6" opacity="0.55"/>';
      o += '<path d="' + ell(cx, cy, 18, 25) + '" fill="none" stroke="{{color_hex}}" stroke-width="1.5"/>';
      o += '<path d="' + ell(cx, cy, 14.6, 20.6) + '" fill="none" stroke="' + INK + '" stroke-width="1.2"/>';

      for (var i = 0; i < 12; i++) {                        // rosette petals, inked into the foil
        var a = (i / 12) * Math.PI * 2;
        var px = cx + Math.cos(a) * 8.4, py = cy + Math.sin(a) * 11.5;
        o += '<ellipse cx="' + r2(px) + '" cy="' + r2(py) + '" rx="2.3" ry="5.4" fill="' +
          (i % 2 ? INK : INK2) + '" transform="rotate(' + r2(a * 180 / Math.PI + 90) + ' ' +
          r2(px) + ' ' + r2(py) + ')"/>';
      }
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="' + circPath(cx, cy, 8) + ' ' +
        diamond(cx, cy, 4.2, 4.6) + '"/>';                  // boss with a foil pip in it
      o += '<circle cx="' + cx + '" cy="' + cy + '" r="1.7" fill="{{color_hex}}"/>';

      o += '<path d="M50 8L53 11.5L50 15L47 11.5Z M50 109L53 105.5L50 102L47 105.5Z" fill="{{color_hex}}"/>';
      return o;
    },

    /* a trading card in a rigid toploader, tilted six degrees — outer shell with
       a rounded mouth, the card edge offset inside it, and one diagonal glare
       band cut across the plastic. The shell is punched to foil (shiny sleeve),
       the card is inked on top of it, and its window, bars and the glare are
       cut back out so they blaze. */
    toploader: function () {
      var o = '', A = -6 * Math.PI / 180, ca = Math.cos(A), sa = Math.sin(A);
      var ox = 50, oy = 58, G = 0.62;

      function p(x, y) {                                    // sleeve plane → window
        return r2(ox + x * ca - y * sa) + ' ' + r2(oy + x * sa + y * ca);
      }
      function poly(pts) {
        var d = '', i;
        for (i = 0; i < pts.length; i++) d += (i ? 'L' : 'M') + p(pts[i][0], pts[i][1]);
        return d + 'Z';
      }
      function box(x0, y0, x1, y1) {
        return poly([[x0, y0], [x1, y0], [x1, y1], [x0, y1]]);
      }
      function band(b0, b1) {                               // full-width diagonal streak
        return poly([[-23, G * 23 + b0], [23, -G * 23 + b0], [23, -G * 23 + b1], [-23, G * 23 + b1]]);
      }

      var shell = 'M' + p(-29, -35) + 'Q' + p(-29, -43) + ' ' + p(-21, -43) +
        'L' + p(21, -43) + 'Q' + p(29, -43) + ' ' + p(29, -35) +
        'L' + p(29, 39) + 'Q' + p(29, 42) + ' ' + p(26, 42) +
        'L' + p(-26, 42) + 'Q' + p(-29, 42) + ' ' + p(-29, 39) + 'Z';
      var edge = box(30.5, -32, 34.5, 39);                  // slab thickness, seen three-quarter

      o += cut([shell, edge, circPath(10, 20, 1.6), circPath(12, 96, 1.6), circPath(88, 26, 1.6)], INK, 0.82);
      o += '<path d="' + edge + '" fill="' + INK3 + '" opacity="0.5"/>';

      /* the card: solid ink, with portrait window, stat badge, serial frame,
         name bars and the glare band all punched clear of one another */
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="' + box(-23, -31, 23, 37) + ' ' +
        box(-18, -26, 12, -6) + ' ' + box(15, -26, 20, -14) + ' ' + band(4, 14) + ' ' +
        box(11, 9, 20, 17) + ' ' + box(-18, 27, 8, 30) + ' ' + box(-18, 32.5, 0, 34.8) + '"/>';
      o += '<path d="' + box(-23, -31, 23, 37) + '" fill="none" stroke="' + L2 +
        '" stroke-width="0.5" opacity="0.5"/>';

      var seam = 'M' + p(-26, -33) + 'Q' + p(-26, -40) + ' ' + p(-19, -40) + 'L' + p(19, -40) +
        'Q' + p(26, -40) + ' ' + p(26, -33) + 'L' + p(26, 39.5) + 'L' + p(-26, 39.5) + 'Z';
      o += '<path d="' + seam + '" fill="none" stroke="' + INK + '" stroke-width="0.9" opacity="0.9"/>';
      o += '<path d="M' + p(-26, -36) + 'L' + p(26, -36) + 'M' + p(-25, -33.5) + 'L' + p(25, -33.5) +
        '" stroke="' + INK + '" stroke-width="1.3" fill="none"/>';

      o += '<path d="' + shell + '" fill="none" stroke="{{color_hex}}" stroke-width="1.6"/>';
      o += '<path d="' + box(11, 9, 20, 17) + '" fill="none" stroke="{{color_hex}}" stroke-width="1"/>';
      return o;
    },
/* three trading cards fanned from a pivot below the window — the two behind
       are inked flat, the top card is cut clean through to bare foil as the
       chase hit, each card edged with a hairline of the customer's accent */
    cardfan: function () {
      var PX = 50, PY = 110, CW = 38, CH = 58, LIFT = 10;

      function corners(deg) {                                  // one card, four rotated points
        var a = deg * Math.PI / 180, c = Math.cos(a), s = Math.sin(a), out = [];
        [[-CW / 2, -LIFT], [CW / 2, -LIFT], [CW / 2, -LIFT - CH], [-CW / 2, -LIFT - CH]]
          .forEach(function (q) {
            out.push([PX + q[0] * c - q[1] * s, PY + q[0] * s + q[1] * c]);
          });
        return out;
      }
      function poly(p) {
        return p.map(function (q, i) {
          return (i ? 'L' : 'M') + r2(q[0]) + ' ' + r2(q[1]);
        }).join('') + 'Z';
      }
      /* keep only the half of a card that is not tucked behind the top card —
         the hidden half is never painted, so the top card's foil stays clean */
      function half(p, ax, lim, less) {
        var out = [], i, a, b, ai, bi, t;
        for (i = 0; i < p.length; i++) {
          a = p[i]; b = p[(i + 1) % p.length];
          ai = less ? a[ax] <= lim : a[ax] >= lim;
          bi = less ? b[ax] <= lim : b[ax] >= lim;
          if (ai) out.push(a);
          if (ai !== bi) {
            t = (lim - a[ax]) / (b[ax] - a[ax]);
            out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
          }
        }
        return out.length > 2 ? poly(out) : '';
      }

      var front = corners(0);
      var x0 = front[0][0], x1 = front[1][0], yTop = front[2][1], yBot = front[0][1];
      var fx0 = x0 + 3.2, fx1 = x1 - 3.2, fy0 = yTop + 3.2, fy1 = yBot - 3.2;
      var face = 'M' + r2(fx0) + ' ' + r2(fy0) + 'H' + r2(fx1) + 'V' + r2(fy1) + 'H' + r2(fx0) + 'Z';
      var o = cut([face], INK, 0.34);                          // smoked ground; the top card blazes

      [-20, 20].forEach(function (deg) {                       // the pair behind, inked flat
        var p = corners(deg), lead = deg < 0 ? 3 : 2, tail = deg < 0 ? 2 : 3, foot = deg < 0 ? 0 : 1;
        [half(p, 0, deg < 0 ? x0 : x1, deg < 0), half(p, 1, yTop, true), half(p, 1, yBot, false)]
          .forEach(function (d) { if (d) { o += '<path d="' + d + '" fill="' + INK + '"/>'; } });
        o += '<path d="M' + r2(p[foot][0]) + ' ' + r2(p[foot][1]) + 'L' + r2(p[lead][0]) + ' ' +
          r2(p[lead][1]) + 'L' + r2(p[tail][0]) + ' ' + r2(p[tail][1]) +
          '" fill="none" stroke="{{color_hex}}" stroke-width="1" opacity="0.9"/>';
      });

      o += '<path fill-rule="evenodd" fill="' + INK + '" d="M0 103H100V117H0Z M8 108h84v2h-84Z"/>';
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="' + poly(front) + ' ' + face + '"/>';
      o += '<path d="' + poly(front) + '" fill="none" stroke="{{color_hex}}" stroke-width="1.5"/>';
      o += '<rect x="' + r2(fx0 + 2.6) + '" y="' + r2(fy0 + 2.6) + '" width="' + r2(fx1 - fx0 - 5.2) +
        '" height="' + r2(fy1 - fy0 - 5.2) + '" fill="none" stroke="' + INK + '" stroke-width="1"/>';
      o += '<path d="M37.4 50.4h7v1.6h-7Z M55.6 50.4h7v1.6h-7Z" fill="' + INK + '"/>';
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="M50 60L61 71L50 82L39 71Z ' +
        circPath(50, 71, 4.4) + '"/>';                         // pip with a foil eye
      o += '<circle cx="50" cy="71" r="4.4" fill="none" stroke="{{color_hex}}" stroke-width="1"/>';
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="M38 86h24v8h-24Z ' +
        'M40.4 88.2h19.2v1.6h-19.2Z M40.4 91h12.6v1.4h-12.6Z"/>';   // nameplate, text in foil
      return o;
    },

    /* a deck seen from a low angle — a bare-foil top face riding nine inked
       edge slices, foil bleeding through every gap, one card proud of the stack */
    deckstack: function () {
      var LIP = 52, BASE = 99, SL = 9, pitch = (BASE - LIP) / SL, ink = 3.9;

      function edge(y) { var t = (y - LIP) / (BASE - LIP); return [19 + t * 4, 81 - t * 4]; }
      function band(y0, y1, dx) {                              // one slice of the stack wall
        var a = edge(y0), b = edge(y1);
        return 'M' + r2(a[0] + dx) + ' ' + r2(y0) + 'H' + r2(a[1] + dx) +
          'L' + r2(b[1] + dx) + ' ' + r2(y1) + 'H' + r2(b[0] + dx) + 'Z';
      }

      var top = 'M34 22H66L81 52H19Z', topIn = 'M37.2 25.3H62.8L74.8 49.3H25.2Z';
      var gaps = [], i, y, jog;
      for (i = 0; i < SL; i++) {
        gaps.push(band(LIP + i * pitch + ink, LIP + (i + 1) * pitch, 0));
      }
      gaps.push(band(BASE, BASE + 1.6, 0));                    // deck lifts off its plinth

      var o = cut([topIn].concat(gaps), INK, 0.34);            // ground smoked, every gap live foil

      for (i = 0; i < 11; i++) {                               // shine throwing off the top face
        var a = -Math.PI / 2 + (i - 5) * 0.16;
        o += '<path d="M50 22L' + r2(50 + Math.cos(a) * 30) + ' ' + r2(22 + Math.sin(a) * 30) +
          '" stroke="' + INK + '" stroke-width="' + (i % 2 ? 0.5 : 1) + '" opacity="0.4"/>';
      }
      for (i = 0; i < SL; i++) {                               // the stack itself
        y = LIP + i * pitch; jog = i === 6 ? 2.8 : 0;
        o += '<path d="' + band(y, y + ink, jog) + '" fill="' + (i % 2 ? INK2 : INK) + '"/>';
      }
      y = LIP + 6 * pitch + ink;
      o += '<path d="M' + r2(edge(y)[0] + 2.8) + ' ' + r2(y) + 'H' + r2(edge(y)[1] + 2.8) +
        '" stroke="{{color_hex}}" stroke-width="0.9" opacity="0.9"/>';
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="M21 100.6H79L86 108.4H14Z ' +
        'M22 103.6h56v1.8h-56Z"/>';                            // plinth, slit to foil
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="' + top + ' ' + topIn + '"/>';
      o += '<path d="' + top + '" fill="none" stroke="{{color_hex}}" stroke-width="1.4"/>';
      o += '<path d="M28 46.4h9v1.8h-9Z M63 46.4h9v1.8h-9Z" fill="' + INK + '"/>';
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="M50 29.5L58.5 38L50 46.5L41.5 38Z ' +
        circPath(50, 38, 3.6) + '"/>';                         // seal mark, foil at its centre
      o += '<circle cx="50" cy="38" r="3.6" fill="none" stroke="{{color_hex}}" stroke-width="1"/>';
      return o;
    },
/* chase-card starburst — an off-centre detonation: ragged ink wedges
       alternating with live-foil gaps, four long spike rays, cut foil core */
    chasehit: function (seed) {
      var rnd = rng(seed || 'chasehit'), cx = 44, cy = 46, o = '';
      var n = 30, r0 = 13.5, i;
      function pt(a, r) { return r2(cx + Math.cos(a) * r) + ' ' + r2(cy + Math.sin(a) * r); }

      /* the burst: every other slot is left unpainted so the foil rips through */
      for (i = 0; i < n; i++) {
        if (i % 2) continue;
        var a0 = (i / n) * Math.PI * 2 - 0.22;
        var a1 = ((i + 1) / n) * Math.PI * 2 - 0.22;
        var am = (a0 + a1) / 2;
        var ra = 44 + rnd() * 40, rb = ra + 10 + rnd() * 26;
        o += '<path d="M' + pt(a0, r0) + 'L' + pt(a0, ra) + 'L' + pt(am, rb) + 'L' + pt(a1, ra) +
          'L' + pt(a1, r0) + 'Z" fill="' + (i % 4 ? INK : INK2) + '"/>';
      }
      /* four spike rays that run clean off the window */
      [0.42, 1.98, 3.4, 5.02].forEach(function (a, k) {
        var w = 0.055 - k * 0.008;
        o += '<path d="M' + pt(a - w, r0) + 'L' + pt(a, 175) + 'L' + pt(a + w, r0) + 'Z" fill="' + INK + '"/>';
      });
      /* thrown debris — ink shards drifting outward through the bare gaps */
      for (i = 0; i < 24; i++) {
        var da = rnd() * Math.PI * 2, dr = 30 + rnd() * 58, ds = 3 + rnd() * 7;
        o += '<path d="M' + pt(da, dr) + 'L' + pt(da + 0.055, dr + ds) + 'L' + pt(da - 0.055, dr + ds) +
          'Z" fill="' + INK + '" opacity="' + r2(0.55 + rnd() * 0.45) + '"/>';
      }
      /* concussion ring — a broken run of ticks, not a closed circle */
      for (i = 0; i < 42; i++) {
        if (i % 3 === 0) continue;
        var ra2 = (i / 42) * Math.PI * 2;
        o += '<path d="M' + pt(ra2, 29.5) + 'L' + pt(ra2, 33.5) + '" stroke="' + INK +
          '" stroke-width="1.2" opacity="0.85"/>';
      }
      /* pinprick sparks in the open foil */
      for (i = 0; i < 13; i++) {
        var sa = rnd() * Math.PI * 2, sr = 22 + rnd() * 60;
        o += '<circle cx="' + r2(cx + Math.cos(sa) * sr) + '" cy="' + r2(cy + Math.sin(sa) * sr) +
          '" r="' + r2(0.6 + rnd() * 1.1) + '" fill="' + L2 + '" opacity="0.6"/>';
      }
      /* the hit itself: a heavy ink collar leaving a bare foil disc inside it */
      o += '<circle cx="' + cx + '" cy="' + cy + '" r="11.4" fill="none" stroke="' + INK + '" stroke-width="2.6"/>';
      o += '<circle cx="' + cx + '" cy="' + cy + '" r="16.4" fill="none" stroke="{{color_hex}}" stroke-width="1.5"/>';
      var star = '';
      for (i = 0; i < 8; i++) {
        var st = -Math.PI / 2 + i * Math.PI / 4;
        star += (i ? 'L' : 'M') + pt(st, i % 2 ? 2.5 : 8.2);
      }
      o += '<path d="' + star + 'Z" fill="' + INK + '"/>';
      o += '<circle cx="' + cx + '" cy="' + cy + '" r="1.5" fill="{{color_hex}}"/>';
      return o;
    },

    /* the grader's reticle — a card-shaped measurement overlay: registration
       crosses and a centring meter cut to foil, brackets, crosshair, edge ticks */
    centering: function () {
      var x0 = 21.5, y0 = 16.5, cw = 57, ch = 80;
      var x1 = r2(x0 + cw), y1 = r2(y0 + ch), mx = 50, my = r2(y0 + ch / 2);
      var holes = [], o = '', i, maj;

      function bar(x, y, w, h) {
        return 'M' + r2(x) + ' ' + r2(y) + 'h' + r2(w) + 'v' + r2(h) + 'h' + r2(-w) + 'Z';
      }
      function reg(x, y, a, t) {                       // a printer's registration cross
        var s = r2(a - t / 2);
        return 'M' + r2(x - a) + ' ' + r2(y - t / 2) + 'h' + s + 'v-' + s + 'h' + t + 'v' + s +
          'h' + s + 'v' + t + 'h-' + s + 'v' + s + 'h-' + t + 'v-' + s + 'h-' + s + 'Z';
      }

      /* the card window is CUT — the whole slab is live foil, only the margin
         is smoked ink, so the measurement linework sits on bare shine */
      holes.push(bar(x0, y0, cw, ch));
      [[10.5, 9.6], [89.5, 9.6], [10.5, 104], [89.5, 104]].forEach(function (c) {
        holes.push(reg(c[0], c[1], 4, 1.5));
      });
      holes.push(bar(24, 102.4, 52, 3.6));             // centring meter, bottom margin
      for (i = 0; i < 13; i++) {                       // serial tick strip, top margin
        holes.push(bar(24 + i * 4.05, 8.2, 2.1, 2.4));
      }
      o += cut(holes, INK, 0.34);

      o += '<rect x="' + x0 + '" y="' + y0 + '" width="' + cw + '" height="' + ch +
        '" fill="none" stroke="' + INK + '" stroke-width="1.5"/>';
      o += '<rect x="' + r2(x0 + 11) + '" y="' + r2(y0 + 15) + '" width="' + r2(cw - 22) + '" height="' +
        r2(ch - 30) + '" fill="none" stroke="' + L2 +
        '" stroke-width="0.55" stroke-dasharray="3 2.4" opacity="0.75"/>';

      /* corner registration brackets */
      [[x0 + 3.6, y0 + 3.6, 1, 1], [x1 - 3.6, y0 + 3.6, -1, 1],
       [x0 + 3.6, y1 - 3.6, 1, -1], [x1 - 3.6, y1 - 3.6, -1, -1]].forEach(function (c) {
        o += '<path d="M' + r2(c[0]) + ' ' + r2(c[1] + c[3] * 13) + 'V' + r2(c[1]) + 'H' + r2(c[0] + c[2] * 13) +
          '" fill="none" stroke="' + INK + '" stroke-width="2.4"/>';
      });

      /* crosshair, broken around the reticle */
      o += '<path d="M' + mx + ' ' + r2(y0 + 4) + 'V' + r2(my - 11) + 'M' + mx + ' ' + r2(my + 11) + 'V' + r2(y1 - 4) +
        'M' + r2(x0 + 4) + ' ' + my + 'H' + r2(mx - 11) + 'M' + r2(mx + 11) + ' ' + my + 'H' + r2(x1 - 4) +
        '" fill="none" stroke="' + INK + '" stroke-width="0.75" opacity="0.9"/>';

      /* edge ticks — finest in L2, every fourth stepped up to ink */
      for (i = 0; i <= 24; i++) {
        maj = i % 4 === 0;
        o += '<path d="M' + r2(x0 + 2.2) + ' ' + r2(y0 + 6 + i * ((ch - 12) / 24)) + 'h' + (maj ? 6.4 : 3.1) +
          '" fill="none" stroke="' + (maj ? INK : L2) + '" stroke-width="' + (maj ? 0.95 : 0.5) + '"/>';
      }
      for (i = 0; i <= 20; i++) {
        maj = i % 4 === 0;
        o += '<path d="M' + r2(x0 + 6 + i * ((cw - 12) / 20)) + ' ' + r2(y1 - 2.2) + 'v-' + (maj ? 6.4 : 3.1) +
          '" fill="none" stroke="' + (maj ? INK : L2) + '" stroke-width="' + (maj ? 0.95 : 0.5) + '"/>';
      }

      /* dimension span across the top of the slab */
      o += '<path d="M' + r2(x0 + 8) + ' ' + r2(y0 + 9.5) + 'H' + r2(x1 - 8) +
        'M' + r2(x0 + 8) + ' ' + r2(y0 + 6.9) + 'v5.2M' + r2(x1 - 8) + ' ' + r2(y0 + 6.9) + 'v5.2' +
        'M' + mx + ' ' + r2(y0 + 6.1) + 'v6.8" fill="none" stroke="' + INK + '" stroke-width="0.7" opacity="0.9"/>';

      /* the reticle */
      o += '<circle cx="' + mx + '" cy="' + my + '" r="9.2" fill="none" stroke="' + INK + '" stroke-width="1.3"/>';
      for (i = 0; i < 4; i++) {
        var a = Math.PI / 4 + i * Math.PI / 2;
        o += '<path d="M' + r2(mx + Math.cos(a) * 6.6) + ' ' + r2(my + Math.sin(a) * 6.6) + 'L' +
          r2(mx + Math.cos(a) * 12) + ' ' + r2(my + Math.sin(a) * 12) + '" stroke="' + INK + '" stroke-width="1"/>';
      }
      o += '<circle cx="' + mx + '" cy="' + my + '" r="4.8" fill="none" stroke="{{color_hex}}" stroke-width="1.5"/>';
      o += '<circle cx="' + mx + '" cy="' + my + '" r="1.1" fill="' + INK + '"/>';

      /* meter graduations on the cut foil bar */
      o += '<path d="M37 102.4v3.6M63 102.4v3.6" fill="none" stroke="' + INK + '" stroke-width="0.9"/>';
      o += '<path d="M50 101.4v5.6" fill="none" stroke="{{color_hex}}" stroke-width="1.7"/>';
      return o;
    },


    /* angular prismatic facets radiating from an off-centre point */
    facets: function (seed) {
      var rnd = rng(seed || 'facets'), cx = 50, cy = 44, o = '';
      var n = 22;
      for (var i = 0; i < n; i++) {
        var a0 = (i / n) * Math.PI * 2 + 0.14;
        var a1 = ((i + 1) / n) * Math.PI * 2 + 0.14;
        if (i % 2) continue;                                  // alternate wedges stay bare foil
        var r = 78 + rnd() * 34;
        o += '<path d="M' + cx + ' ' + cy +
          'L' + r2(cx + Math.cos(a0) * r) + ' ' + r2(cy + Math.sin(a0) * r) +
          'L' + r2(cx + Math.cos(a1) * r) + ' ' + r2(cy + Math.sin(a1) * r) + 'Z" fill="' +
          (i % 4 ? INK2 : INK) + '"/>';
      }
      for (i = 0; i < n; i++) {
        var a = (i / n) * Math.PI * 2 + 0.14;
        o += '<path d="M' + cx + ' ' + cy + 'L' + r2(cx + Math.cos(a) * 96) + ' ' +
          r2(cy + Math.sin(a) * 96) + '" stroke="' + INK + '" stroke-width="' +
          (i % 3 ? 0.5 : 1.1) + '" opacity="0.9"/>';
      }
      [11, 19, 29, 41].forEach(function (rr, i) {
        o += '<circle cx="' + cx + '" cy="' + cy + '" r="' + rr + '" fill="none" stroke="' +
          (i === 1 ? '{{color_hex}}' : INK) + '" stroke-width="' + (1.0 - i * 0.15) + '" opacity="0.75"/>';
      });
      o += '<circle cx="' + cx + '" cy="' + cy + '" r="6.5" fill="' + INK + '"/>';
      o += '<circle cx="' + cx + '" cy="' + cy + '" r="3.2" fill="{{color_hex}}"/>';
      return o;
    },

    /* organic marble veining — the calmest design, mostly bare foil */
    veins: function (seed) {
      var rnd = rng(seed || 'veins'), o = '';
      for (var v = 0; v < 11; v++) {
        var x = rnd() * W, y = -4, d = 'M' + r2(x) + ' ' + r2(y);
        for (var s = 0; s < 9; s++) {
          x += (rnd() - 0.5) * 34; y += H / 8;
          d += 'Q' + r2(x + (rnd() - 0.5) * 22) + ' ' + r2(y - H / 16) + ' ' + r2(x) + ' ' + r2(y);
        }
        o += '<path d="' + d + '" fill="none" stroke="' + (v % 4 ? INK : '{{color_hex}}') +
          '" stroke-width="' + r2(0.5 + rnd() * 1.9) + '" opacity="' + r2(0.4 + rnd() * 0.5) + '"/>';
      }
      for (var i = 0; i < 26; i++) {
        o += '<circle cx="' + r2(rnd() * W) + '" cy="' + r2(rnd() * H) + '" r="' +
          r2(0.7 + rnd() * 1.3) + '" fill="' + INK + '" opacity="0.5"/>';
      }
      return o;
    },

    /* circuit city — traces, vias, towers, horizon */
    /* CYBER GRID — a real circuit board, not a scattered mess.
       Rebuilt 2026-07-28: the old version stacked a perspective floor, a
       random skyline AND random traces into one 19mm window — three competing
       systems that printed as noise. Now it is ONE idea drawn deliberately:
       an inked board with a foil chip at its heart, orthogonal traces running
       out to pads on a strict lane grid. Everything snaps to the same pitch,
       so at seal scale it reads as circuitry instead of scribble. */
    grid: function (seed) {
      var o = '', H2 = [];                        // H2 = holes punched to foil
      var cx = 50, cy = 58.5;
      var chipW = 30, chipH = 30;
      var chipX = cx - chipW / 2, chipY = cy - chipH / 2;

      function bar(x, y, w, h) {                  // axis-aligned foil cut
        return 'M' + r2(x) + ' ' + r2(y) + 'h' + r2(w) + 'v' + r2(h) + 'h' + r2(-w) + 'Z';
      }

      /* THE MATERIAL RULE: the board is ink, every conductor is CUT OUT so it
         prints as bare holographic foil. Painting traces in accent ink over an
         accent-tinted board is what made this design read as mud — a foil
         circuit on a dark board is legible at 19mm and shimmers when tilted. */
      H2.push(bar(chipX, chipY, chipW, chipH));                       // die
      H2.push(bar(chipX - 6, chipY - 5.5, chipW + 12, 1.8));          // guard rails
      H2.push(bar(chipX - 6, chipY + chipH + 3.7, chipW + 12, 1.8));

      /* trace lanes: strict pitch, one dogleg each, terminating in a pad.
         Deterministic — no RNG — so every Cyber Grid seal is identical. */
      var TR = 2.0, PAD = 3.4;
      var lanes = [10, 20, 30, 40, 78, 88, 98, 108];
      lanes.forEach(function (y, i) {
        var left = i % 2 === 0;
        var run1 = 20 + (i % 3) * 7;
        var run2 = 13 + ((i + 1) % 3) * 6;
        var step = y < cy ? 7 : -7;
        var x0 = left ? 5 : 95 - run1;
        H2.push(bar(x0, y, run1, TR));                                 // first run
        var jx = left ? x0 + run1 - TR : x0;                           // vertical jog
        H2.push(bar(jx, step > 0 ? y : y + step, TR, Math.abs(step)));
        var x2 = left ? jx : jx + TR - run2;                           // second run
        H2.push(bar(x2, y + step, run2, TR));
        var px = left ? 5 : 95 - PAD;                                  // edge pad
        H2.push(bar(px, y + TR / 2 - PAD / 2, PAD, PAD));
      });

      /* chip pins: even ticks on all four edges */
      for (var p = 0; p < 6; p++) {
        var t = chipX + 3.5 + p * ((chipW - 7) / 5);
        H2.push(bar(t, chipY - 4.2, 1.7, 3.4));
        H2.push(bar(t, chipY + chipH + 0.8, 1.7, 3.4));
        var s = chipY + 3.5 + p * ((chipH - 7) / 5);
        H2.push(bar(chipX - 4.2, s, 3.4, 1.7));
        H2.push(bar(chipX + chipW + 0.8, s, 3.4, 1.7));
      }

      /* scatter vias — sparse, off the chip, on a fixed pattern */
      [[12, 52], [88, 52], [16, 66], [84, 66], [50, 8], [50, 110]].forEach(function (v) {
        H2.push(circPath(v[0], v[1], 1.9));
      });

      o += cut(H2, INK);

      /* back INTO the foil die: an inked frame + index dot so the chip reads
         as a component, and two contact lines so it isn't a blank square */
      o += '<rect x="' + r2(chipX + 3.4) + '" y="' + r2(chipY + 3.4) + '" width="' + r2(chipW - 6.8) +
           '" height="' + r2(chipH - 6.8) + '" fill="none" stroke="' + INK + '" stroke-width="1.3"/>';
      o += '<circle cx="' + r2(chipX + 7.2) + '" cy="' + r2(chipY + 7.2) + '" r="2" fill="' + INK + '"/>';
      o += '<path d="M' + r2(chipX + 9) + ' ' + r2(cy + 3) + 'h' + r2(chipW - 18) +
           'M' + r2(chipX + 9) + ' ' + r2(cy + 6) + 'h' + r2(chipW - 22) +
           '" stroke="' + INK + '" stroke-width="1.1" fill="none"/>';
      return o;
    },

    /* collapsing spiral — arms of ink, core of bare foil */
    spiral: function (seed) {
      var rnd = rng(seed || 'spiral'), cx = 50, cy = 50, o = '';
      o += cut([circPath(cx, cy, 13)], INK);
      for (var arm = 0; arm < 5; arm++) {
        var d = '', d2 = '';
        for (var t = 0; t <= 46; t++) {
          var th = t / 46 * 3.5 + arm * (Math.PI * 2 / 5);
          var r = 12 + t / 46 * 62;
          d += (t ? 'L' : 'M') + r2(cx + Math.cos(th) * r) + ' ' + r2(cy + Math.sin(th) * r * 0.92);
          var r2b = r + 9 + t * 0.16;
          d2 = (t ? 'L' : 'M') + r2(cx + Math.cos(th) * r2b) + ' ' + r2(cy + Math.sin(th) * r2b * 0.92) + d2;
        }
        o += '<path d="' + d + ' ' + d2 + 'Z" fill="' + (arm % 2 ? INK2 : INK) + '" opacity="0.95"/>';
      }
      for (var i = 0; i < 40; i++) {                                   // stars = tiny foil gaps
        var a = rnd() * Math.PI * 2, rr = 16 + rnd() * 58;
        o += '<circle cx="' + r2(cx + Math.cos(a) * rr) + '" cy="' + r2(cy + Math.sin(a) * rr * 0.95) +
          '" r="' + r2(0.7 + rnd() * 1.1) + '" fill="' + L1 + '" opacity="' + r2(0.4 + rnd() * 0.6) + '"/>';
      }
      o += '<circle cx="' + cx + '" cy="' + cy + '" r="17" fill="none" stroke="{{color_hex}}" stroke-width="1.1" opacity="0.85"/>';
      return o;
    },

    /* synthwave horizon — sliced sun, ridges, grid */
    sunset: function () {
      var o = '', slices = [];
      for (var i = 0; i < 7; i++) {                                    // sun slices = foil gaps
        var y = 20 + i * 5.4, h = 2.0 + i * 0.42;
        slices.push('M18 ' + r2(y) + 'h64v' + r2(h) + 'h-64Z');
      }
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="M0 0H' + W + 'V64H0Z ' +
        circPath(50, 36, 27) + '"/>';
      o += '<path fill="' + INK + '" d="' + slices.join(' ') + '"/>';
      o += '<circle cx="50" cy="36" r="27" fill="none" stroke="{{color_hex}}" stroke-width="1.2"/>';
      o += '<path d="M0 62L22 46L36 58L52 40L68 60L84 48L100 62V70H0Z" fill="' + INK3 + '"/>';
      o += '<path d="M0 66H' + W + 'V' + H + 'H0Z" fill="' + INK + '"/>';
      for (i = 1; i < 8; i++) {
        var yy = 66 + Math.pow(i / 8, 1.8) * 51;
        o += '<path d="M0 ' + r2(yy) + 'H' + W + '" stroke="{{color_hex}}" stroke-width="0.55" opacity="0.8"/>';
      }
      for (i = -3; i <= 11; i++) {
        o += '<path d="M50 66L' + r2(i * 24 - 20) + ' ' + H + '" stroke="{{color_hex}}" stroke-width="0.5" opacity="0.55"/>';
      }
      return o;
    },

    /* heraldic crest — layered shield, laurels, banner */
    crest: function () {
      var shield = 'M50 18L78 27V56C78 74 64 86 50 93C36 86 22 74 22 56V27Z';
      var o = cut([shield], INK, 0.42);
      o += '<path d="' + shield + '" fill="none" stroke="{{color_hex}}" stroke-width="1.6"/>';
      o += '<path d="M50 24L72 31V55C72 70 60 80 50 86C40 80 28 70 28 55V31Z" fill="none" stroke="' +
        L2 + '" stroke-width="0.7" opacity="0.85"/>';
      /* laurel branches */
      for (var s = -1; s <= 1; s += 2) {
        for (var i = 0; i < 7; i++) {
          var y = 34 + i * 8.2, x = 50 + s * (30 + i * 1.4);
          o += '<ellipse cx="' + r2(x) + '" cy="' + r2(y) + '" rx="5.2" ry="2.4" fill="' + INK +
            '" transform="rotate(' + (s * (28 + i * 4)) + ' ' + r2(x) + ' ' + r2(y) + ')"/>';
        }
      }
      /* trophy cup, cut so the foil fills it */
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="M34 38h32v10a16 16 0 0 1-32 0Z ' +
        'M39 42h22v6a11 11 0 0 1-22 0Z"/>';
      o += '<path d="M46 64h8v9h-8Z M38 73h24v4h-24Z" fill="' + INK + '"/>';
      o += '<path d="M34 40c-6 0-9 4-9 8s4 8 9 8M66 40c6 0 9 4 9 8s-4 8-9 8" fill="none" stroke="' +
        INK + '" stroke-width="2.2"/>';
      /* banner */
      o += '<path d="M14 92h72l-8 9 8 9H14l8-9Z" fill="' + INK + '"/>';
      o += '<path d="M18 96h64" stroke="{{color_hex}}" stroke-width="1.2" opacity="0.9"/>';
      return o;
    },

    /* ornate crown on a plinth */
    crown: function () {
      var crown = 'M22 62L16 26L32 40L50 14L68 40L84 26L78 62Z';
      var o = cut([crown, circPath(50, 30, 4.5), circPath(28, 45, 3.4), circPath(72, 45, 3.4)], INK);
      o += '<path d="' + crown + '" fill="none" stroke="{{color_hex}}" stroke-width="1.7"/>';
      o += '<path d="M20 66h60v7h-60Z" fill="' + INK + '"/>';
      o += '<path d="M22 69h56" stroke="{{color_hex}}" stroke-width="1" opacity="0.9"/>';
      for (var i = 0; i < 18; i++) {                                   // ceremonial rays behind
        var a = -Math.PI / 2 + (i - 8.5) * 0.13;
        o += '<path d="M50 40L' + r2(50 + Math.cos(a) * 92) + ' ' + r2(40 + Math.sin(a) * 92) +
          '" stroke="' + INK + '" stroke-width="' + (i % 2 ? 0.5 : 1.0) + '" opacity="0.35"/>';
      }
      o += '<path d="M14 80h72l-7 8 7 8H14l7-8Z" fill="' + INK + '"/>';
      o += '<path d="M18 84h64" stroke="' + L2 + '" stroke-width="0.8" opacity="0.8"/>';
      o += '<path d="M26 100h48v3h-48Z M32 106h36v2.6h-36Z" fill="' + INK + '" opacity="0.9"/>';
      return o;
    },

    /* winged silhouette — used for guardian / phoenix until art lands */
    wings: function (seed) {
      var rnd = rng(seed || 'wings'), o = '';
      var body = 'M50 30c5 0 8 4 8 9s-3 8-8 8-8-3-8-8 3-9 8-9Z' +
                 'M44 48h12l5 34H39Z';
      var wingL = '', wingR = '';
      for (var i = 0; i < 7; i++) {
        var sp = 8 + i * 5.4, len = 30 - i * 2.2;
        wingL += 'M44 ' + r2(46 + i * 5) + 'q-' + r2(len) + ' -' + r2(sp) + ' -' + r2(len + 8) + ' ' + r2(4 + i) +
                 'q' + r2(len * 0.5) + ' ' + r2(sp * 0.3) + ' ' + r2(len + 8) + ' ' + r2(3) + 'Z';
        wingR += 'M56 ' + r2(46 + i * 5) + 'q' + r2(len) + ' -' + r2(sp) + ' ' + r2(len + 8) + ' ' + r2(4 + i) +
                 'q-' + r2(len * 0.5) + ' ' + r2(sp * 0.3) + ' -' + r2(len + 8) + ' ' + r2(3) + 'Z';
      }
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="M0 0H' + W + 'V' + H + 'H0Z ' +
        circPath(50, 30, 15) + '"/>';
      o += '<path d="' + wingL + '" fill="' + INK + '" opacity="0.95"/>';
      o += '<path d="' + wingR + '" fill="' + INK + '" opacity="0.95"/>';
      o += '<path d="' + body + '" fill="' + INK2 + '" stroke="' + L2 + '" stroke-width="0.6"/>';
      o += '<circle cx="50" cy="30" r="15" fill="none" stroke="{{color_hex}}" stroke-width="1.3"/>';
      for (i = 0; i < 22; i++) {
        var a = rnd() * Math.PI * 2, rr = 34 + rnd() * 48;
        o += '<circle cx="' + r2(50 + Math.cos(a) * rr * 0.8) + '" cy="' + r2(48 + Math.sin(a) * rr * 0.7) +
          '" r="' + r2(0.7 + rnd()) + '" fill="' + L1 + '" opacity="0.55"/>';
      }
      return o;
    },

    /* open ring — a calm bed for a customer logo */
    ring: function () {
      var o = cut([circPath(50, 52, 26)], INK, 0.42);
      o += '<circle cx="50" cy="52" r="30" fill="none" stroke="{{color_hex}}" stroke-width="1.3"/>';
      o += '<circle cx="50" cy="52" r="34" fill="none" stroke="' + L2 + '" stroke-width="0.6" opacity="0.7"/>';
      for (var i = 0; i < 48; i++) {
        var a = (i / 48) * Math.PI * 2;
        o += '<path d="M' + r2(50 + Math.cos(a) * 38) + ' ' + r2(52 + Math.sin(a) * 38) +
          'L' + r2(50 + Math.cos(a) * 41.5) + ' ' + r2(52 + Math.sin(a) * 41.5) +
          '" stroke="' + L2 + '" stroke-width="0.7" opacity="0.6"/>';
      }
      o += '<path d="M18 100h64" stroke="{{color_hex}}" stroke-width="1" opacity="0.85"/>';
      return o;
    },

    /* woven twill — technical badge plate */
    carbon: function () {
      var o = '<path d="M0 0H' + W + 'V' + H + 'H0Z" fill="' + INK + '" opacity="0.42"/>', c = 11;
      for (var y = -c; y < H + c; y += c) {
        for (var x = -c; x < W + c; x += c) {
          var alt = ((x / c | 0) + (y / c | 0)) % 2 === 0;
          o += '<rect x="' + r2(x) + '" y="' + r2(y) + '" width="' + r2(c * 0.88) + '" height="' +
            r2(c * 0.88) + '" rx="1.4" fill="none" stroke="' + (alt ? L2 : '{{color_hex}}') +
            '" stroke-width="0.6" opacity="' + (alt ? 0.42 : 0.55) + '" transform="rotate(' +
            (alt ? 14 : -14) + ' ' + r2(x + c / 2) + ' ' + r2(y + c / 2) + ')"/>';
        }
      }
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="M14 34h72v50h-72Z ' +
        'M20 40h60v38h-60Z"/>';
      o += '<rect x="14" y="34" width="72" height="50" rx="3" fill="none" stroke="{{color_hex}}" stroke-width="1.4"/>';
      return o;
    },

    /* scaled coil — used for the dragon until art lands */
    scales: function (seed) {
      var rnd = rng(seed || 'scales'), o = '';
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="M0 0H' + W + 'V' + H + 'H0Z ' +
        'M50 16c22 0 34 16 34 32 0 22-18 30-34 30s-34-8-34-30c0-16 12-32 34-32Z"/>';
      for (var row = 0; row < 9; row++) {                              // scale rows in ink
        for (var col = 0; col < 8; col++) {
          var x = 14 + col * 10.4 + (row % 2 ? 5.2 : 0), y = 22 + row * 7.4;
          o += '<path d="M' + r2(x) + ' ' + r2(y) + 'q5.2 0 5.2 5.6q0 5.6-5.2 5.6q-5.2 0-5.2-5.6q0-5.6 5.2-5.6Z" fill="' +
            (((row + col) % 3) ? INK : INK3) + '" opacity="' + r2(0.55 + rnd() * 0.4) + '"/>';
        }
      }
      o += '<path d="M18 92q32 -14 64 0" fill="none" stroke="{{color_hex}}" stroke-width="1.6"/>';
      o += '<path fill-rule="evenodd" fill="' + INK + '" d="M26 30h48v22h-48Z ' +
        'M44 36q6 0 6 5t-6 5q-6 0-6-5t6-5Z"/>';                        // eye = foil gap
      o += '<circle cx="44" cy="41" r="6" fill="none" stroke="{{color_hex}}" stroke-width="1.1"/>';
      for (var i = 0; i < 9; i++) {                                    // spine ridge
        o += '<path d="M' + r2(24 + i * 6.6) + ' 100l3.4-7 3.4 7Z" fill="' + INK + '"/>';
      }
      return o;
    }
  };

  /* Illustrated fragments land here (see the design workflow). A design falls
     back to its motif until its illustration exists, so nothing is ever blank. */
  var baked = {};

  function get(key, motifName, seed) {
    if (baked[key]) return baked[key];
    var m = motif[motifName];
    return m ? m(seed || key) : '';
  }

  /* The helpers are exported, not private, so the genre packs that extend
     SS_ART.motif draw with these exact primitives. Re-implementing cut()
     per file is how a motif quietly stops obeying the material rule. */
  return { baked: baked, motif: motif, get: get, INK: INK, INK2: INK2, INK3: INK3,
           L1: L1, L2: L2, W: W, H: H, cut: cut, rng: rng, circPath: circPath, r2: r2 };
})();
